import { UM } from '../_components/tokens';
import { SourceBadge } from './primitives';
import type { Layer, Step, StepId } from './types';

// ─── Chip de filtre par étape ────────────────────────────────────────────────

function StepChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 12,
        border: `1px solid ${active ? UM.accent : UM.border}`,
        background: active ? UM.accent : UM.bg,
        color: active ? '#fff' : UM.textMuted,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        lineHeight: 1.2,
      }}
    >
      {label}
    </button>
  );
}

// ─── Ligne de la bibliothèque (toggle-select avec feedback) ──────────────────

function PickerRow({
  layer, stepLabel, selected, onToggle,
}: {
  layer: Layer; stepLabel: string; selected: boolean; onToggle: () => void;
}) {
  return (
    <div
      onClick={onToggle}
      title={selected ? 'Déjà dans votre sélection — cliquez pour retirer' : 'Cliquez pour ajouter à votre sélection'}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 16px', cursor: 'pointer',
        background: 'transparent',
        opacity: selected ? 0.55 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = UM.bgHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: selected ? '#e5eef4' : UM.accentLight,
        color: selected ? UM.textMuted : UM.accent,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {selected ? (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path d="M1 4.5 4 7.5 10 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1 }}>+</span>
        )}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, color: UM.text, fontWeight: 400 }}>
          {layer.label}
        </span>
        <span style={{ display: 'block', fontSize: 10, color: UM.textDim, marginTop: 1 }}>
          {stepLabel}
        </span>
      </span>
      {selected && (
        <span style={{ fontSize: 10, fontWeight: 600, color: UM.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          Ajoutée
        </span>
      )}
      <SourceBadge source={layer.source} />
    </div>
  );
}

// ─── Bibliothèque de couches ─────────────────────────────────────────────────

export interface LibraryPickerProps {
  steps: Step[];
  library: Set<string>;
  results: Layer[];
  search: string;
  stepFilter: StepId | null;
  catalogSize: number;
  librarySize: number;
  onSearch: (s: string) => void;
  onSetStepFilter: (s: StepId | null) => void;
  onToggle: (id: string) => void;
  onClose: () => void;
}

export function LibraryPicker(props: LibraryPickerProps) {
  const stepLabel = (id: StepId) => {
    const s = props.steps.find((x) => x.id === id);
    return s ? `${s.number}. ${s.title}` : '';
  };

  return (
    <div style={{
      width: 340, minWidth: 340, background: UM.bg,
      borderRight: `1px solid ${UM.border}`,
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', zIndex: 15,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${UM.border}`, background: UM.bgHeader, flexShrink: 0 }}>
        <button
          onClick={props.onClose}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.accent, fontSize: 12, padding: 0, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          ← Retour à ma sélection
        </button>

        <div style={{ fontWeight: 700, fontSize: 15, color: UM.text, marginBottom: 10 }}>
          Bibliothèque de couches
        </div>

        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={props.search}
            onChange={(e) => props.onSearch(e.target.value)}
            placeholder="Rechercher une couche…"
            style={{
              width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6,
              border: `1px solid ${UM.border}`, fontSize: 13, background: UM.bg, color: UM.text,
              boxSizing: 'border-box',
            }}
          />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: UM.textDim, fontSize: 13 }}>⌕</span>
        </div>

        <div style={{ fontSize: 11, color: UM.textMuted, marginTop: 6 }}>
          {props.stepFilter || props.search.trim()
            ? `${props.results.length} couche${props.results.length > 1 ? 's' : ''} sur ${props.catalogSize}`
            : `${props.catalogSize} couches disponibles — ${props.librarySize} dans votre sélection`}
        </div>
      </div>

      {/* Chips de filtre par étape */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6,
        padding: '10px 16px', borderBottom: `1px solid ${UM.border}`, background: UM.bg,
      }}>
        {props.steps.map((s) => (
          <StepChip
            key={s.id}
            label={s.short}
            active={props.stepFilter === s.id}
            onClick={() => props.onSetStepFilter(props.stepFilter === s.id ? null : s.id)}
          />
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {props.results.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 13, color: UM.textDim }}>
            Aucune couche ne correspond à votre recherche.
          </div>
        ) : (
          props.results.map((layer) => (
            <PickerRow
              key={layer.id}
              layer={layer}
              stepLabel={stepLabel(layer.steps[0])}
              selected={props.library.has(layer.id)}
              onToggle={() => props.onToggle(layer.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
